import { useState, useEffect } from "react";
import {
    useCreateFlyerType,
    useUpdateFlyerType,
    type FlyerType,
    type FlyerTypeCreateRequest,
    type FlyerTypeUpdateRequest,
} from "../api/flyerTypes";
import { ImageUpload } from "../components/ImageUpload";
import { showError } from "../shared/toast";
import "./flyer-type-form.css";

interface FlyerTypeManagementFormProps {
    flyerType?: FlyerType;
    onSuccess?: () => void;
    onCancel?: () => void;
}

export const FlyerTypeManagementForm = ({
    flyerType,
    onSuccess,
    onCancel,
}: FlyerTypeManagementFormProps): JSX.Element => {
    const isEditMode = !!flyerType;
    const [formData, setFormData] = useState<FlyerTypeCreateRequest>({
        name: flyerType?.name || "",
        summary: flyerType?.summary || "",
        imageUrl: flyerType?.imageUrl || "",
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const createMutation = useCreateFlyerType();
    const updateMutation = useUpdateFlyerType();

    useEffect(() => {
        if (flyerType) {
            setFormData({
                name: flyerType.name,
                summary: flyerType.summary || "",
                imageUrl: flyerType.imageUrl || "",
            });
        }
    }, [flyerType]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name || formData.name.trim() === "") {
            newErrors.name = "Namn är obligatoriskt";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        try {
            if (isEditMode && flyerType) {
                const updateRequest: FlyerTypeUpdateRequest = {
                    name: formData.name,
                    summary: formData.summary || undefined,
                    imageUrl: formData.imageUrl || undefined,
                };
                await updateMutation.mutateAsync({
                    id: flyerType.id,
                    request: updateRequest,
                });
            } else {
                // Convert empty strings to undefined for optional fields
                const createRequest: FlyerTypeCreateRequest = {
                    name: formData.name,
                    summary: formData.summary?.trim() || undefined,
                    imageUrl: formData.imageUrl?.trim() || undefined,
                };
                await createMutation.mutateAsync(createRequest);
            }
            onSuccess?.();
        } catch (error: any) {
            if (error?.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else {
                const message =
                    error?.response?.data?.message || "Ett fel uppstod";
                showError("Kunde inte spara flygbladet", message);
            }
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const isLoading = createMutation.isPending || updateMutation.isPending;

    return (
        <form className="flyer-type-form" onSubmit={handleSubmit}>
            <h3>{isEditMode ? "Redigera flygblad" : "Skapa nytt flygblad"}</h3>

            <div className="form-group">
                <label htmlFor="name">
                    Namn <span aria-label="obligatoriskt">*</span>
                </label>
                <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "name-error" : undefined}
                    className={errors.name ? "error-field" : ""}
                    placeholder="T.ex. Vårkampanj 2024"
                />
                {errors.name && (
                    <span id="name-error" role="alert" className="error-message">
                        {errors.name}
                    </span>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="summary">Beskrivning</label>
                <textarea
                    id="summary"
                    name="summary"
                    value={formData.summary}
                    onChange={handleChange}
                    rows={3}
                    aria-invalid={!!errors.summary}
                    aria-describedby={errors.summary ? "summary-error" : undefined}
                    className={errors.summary ? "error-field" : ""}
                    placeholder="Kort beskrivning av flygbladet"
                />
                {errors.summary && (
                    <span id="summary-error" role="alert" className="error-message">
                        {errors.summary}
                    </span>
                )}
            </div>

            <div className="form-group">
                <label>Bild</label>
                <ImageUpload
                    value={formData.imageUrl}
                    onChange={(url) => {
                        setFormData((prev) => ({ ...prev, imageUrl: url }));
                    }}
                    disabled={isLoading}
                />
                <p className="form-hint">
                    Ladda upp en bild för flygbladet. Bilden kopplas till flygbladet.
                </p>
            </div>

            <div className="form-actions">
                <button
                    type="button"
                    className="button button--secondary"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Avbryt
                </button>
                <button
                    type="submit"
                    className="button button--primary"
                    disabled={isLoading}
                >
                    {isLoading ? "Sparar..." : isEditMode ? "Spara" : "Skapa"}
                </button>
            </div>
        </form>
    );
};